import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindCertificateByIdQueryResponse,
  FindCertificateByIdPathParams,
} from "../types/FindCertificateById"

type FindCertificateByIdClient = typeof client<
  FindCertificateByIdQueryResponse,
  Error,
  never
>
type FindCertificateById = {
  data: FindCertificateByIdQueryResponse
  error: Error
  request: never
  pathParams: FindCertificateByIdPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindCertificateByIdClient>>
  client: {
    parameters: Partial<Parameters<FindCertificateByIdClient>[0]>
    return: Awaited<ReturnType<FindCertificateByIdClient>>
  }
}
export const findCertificateByIdQueryKey = (
  id: FindCertificateByIdPathParams["id"]
) =>
  [
    { url: "/v1/certificate/find-certificate-by-id/:id", params: { id } },
  ] as const
export type FindCertificateByIdQueryKey = ReturnType<
  typeof findCertificateByIdQueryKey
>
export function findCertificateByIdQueryOptions<
  TData = FindCertificateById["response"],
  TQueryData = FindCertificateById["response"],
>(
  id: FindCertificateByIdPathParams["id"],
  options: FindCertificateById["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindCertificateById["response"],
    FindCertificateById["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findCertificateByIdQueryKey(id)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindCertificateById["data"],
        FindCertificateById["error"]
      >({
        method: "get",
        url: `/v1/certificate/find-certificate-by-id/${id}`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @link /v1/certificate/find-certificate-by-id/:id
 */
export function useFindCertificateById<
  TData = FindCertificateById["response"],
  TQueryData = FindCertificateById["response"],
  TQueryKey extends QueryKey = FindCertificateByIdQueryKey,
>(
  id: FindCertificateByIdPathParams["id"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindCertificateById["response"],
        FindCertificateById["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindCertificateById["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindCertificateById["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findCertificateByIdQueryKey(id)
  const query = useQuery<
    FindCertificateById["data"],
    FindCertificateById["error"],
    TData,
    any
  >({
    ...findCertificateByIdQueryOptions<TData, TQueryData>(id, clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindCertificateById["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
